/**
 * JS Style guide.
 * 
 * Stores lots of variables that relate to the site's style guide.
 */

/** Design system ------------------------------------------- */

/**
 * GENERAL TODOS
 * 
 * - Make everything an array of objects.
 *   We could then use getter functions to return things by key or turn them into options.
 * 
 * - Think through whitelisting, blacklisting, and re-mapping labels, etc. for these.
 */

/**
 * The atomic spacing.
 */
export const atomicSpacing = 8

/**
 * Responsive Breakpoints.
 * Assumes mobile-first approach with each being inclusive min-width.
 */
export const breakpoints = {
    default: {
        label     : 'Mobile (Default)',
        threshold : 0
    },
    portraitTablet: {
        label     : 'Tablet (Portrait)',
        threshold : 768
    },
    landscapeTablet: {
        label     : 'Tablet (Landscape)',
        threshold : 1024
    },
    laptop: {
        label     : 'Laptop',
        threshold : 1280,
    },
    desktop: {
        label     : 'Desktop',
        threshold : 1440
    },
    lg: {
        label     : 'Large',
        threshold : 1920
    },
    xl: {
        label     : 'Extra Large',
        threshold : 2560
    },
    longBoi: {
        label     : 'Long Boi',
        threshold : 3440
    }
}

/**
 * The default breakpoint.
 */
export const defaultBreakpoint = 'default'

/**
 * The breakpoint best associated with the editor experience.
 */
export const editorExperienceBreakpoint = 'laptop'

/**
 * Color options.
 * 
 * All brand colors in the theme.
 * 
 * TODO: Make this an array of objects.
 * VISION: Make these accept colorBox params.
 */
export const colors = {

    // Expression

    // Orange
    'wOrangePlus5'  : { hex: '#fff3db', label: 'W Orange +5' },
    'wOrangePlus4'  : { hex: '#ffdeb0', label: 'W Orange +4' },
    'wOrangePlus3'  : { hex: '#ffc487', label: 'W Orange +3' },
    'wOrangePlus2'  : { hex: '#ffa560', label: 'W Orange +2' },
    'wOrangePlus1'  : { hex: '#ff823b', label: 'W Orange +1' },
    'wOrange0'      : { hex: '#ff5c18', label: 'W Orange 0 (Base)' },
    'wOrangeMinus1' : { hex: '#d34701', label: 'W Orange -1' },
    'wOrangeMinus2' : { hex: '#a73400', label: 'W Orange -2' },
    'wOrangeMinus3' : { hex: '#7b2400', label: 'W Orange -3' },
    'wOrangeMinus4' : { hex: '#501600', label: 'W Orange -4' },
    'wOrangeMinus5' : { hex: '#240a00', label: 'W Orange -5' },

    // Teal
    'wTealPlus8'  : { hex: '#edf0f1', label: 'W Teal +8' },
    'wTealPlus7'  : { hex: '#d0d7da', label: 'W Teal +7' },
    'wTealPlus6'  : { hex: '#b2bec3', label: 'W Teal +6' },
    'wTealPlus5'  : { hex: '#95a6ab', label: 'W Teal +5' },
    'wTealPlus4'  : { hex: '#788e94', label: 'W Teal +4' },
    'wTealPlus3'  : { hex: '#5b767c', label: 'W Teal +3' },
    'wTealPlus2'  : { hex: '#3e5e65', label: 'W Teal +2' },
    'wTealPlus1'  : { hex: '#21464d', label: 'W Teal +1' },
    'wTeal0'      : { hex: '#042e35', label: 'W Teal 0 (Base)' },
    'wTealMinus1' : { hex: '#00272e', label: 'W Teal -1' },
    'wTealMinus2' : { hex: '#001b1f', label: 'W Teal -2' },

    // Aqua
    'aquaPlus6'  : { hex: '#f1f6f7', label: 'Aqua +6' },
    'aquaPlus5'  : { hex: '#d3e2e6', label: 'Aqua +5' },
    'aquaPlus4'  : { hex: '#b5ced5', label: 'Aqua +4' },
    'aquaPlus3'  : { hex: '#97bcc3', label: 'Aqua +3' },
    'aquaPlus2'  : { hex: '#7aa9b1', label: 'Aqua +2' },
    'aquaPlus1'  : { hex: '#5c979f', label: 'Aqua +1' },
    'aqua0'      : { hex: '#3f858c', label: 'Aqua 0 (Base)' },
    'aquaMinus1' : { hex: '#2f7078', label: 'Aqua -1' },
    'aquaMinus2' : { hex: '#205a62', label: 'Aqua -2' },
    'aquaMinus3' : { hex: '#144249', label: 'Aqua -3' },
    'aquaMinus4' : { hex: '#0b292e', label: 'Aqua -4' },

    // Neutral

    // Light
    'white'       : { hex: '#ffffff', label: 'White' },
    'lightPlus3'  : { hex: '#f7f8f9', label: 'Neutral Light +3' },
    'lightPlus2'  : { hex: '#f0f2f5', label: 'Neutral Light +2' },
    'lightPlus1'  : { hex: '#dee1e5', label: 'Neutral Light +1' },
    'light0'      : { hex: '#b0b7bf', label: 'Neutral Light 0 (Base)' },
    'lightMinus1' : { hex: '#878f99', label: 'Neutral Light -1' },
    'lightMinus2' : { hex: '#6e7680', label: 'Neutral Light -2' },
    'darkPlus2'   : { hex: '#545c66', label: 'Neutral Dark +2' },
    'darkPlus1'   : { hex: '#3d444c', label: 'Neutral Dark +1' },
    'dark0'       : { hex: '#2b323a', label: 'Neutral Dark 0 (Base)' },
    'darkMinus1'  : { hex: '#20262d', label: 'Neutral Dark -1' },
    'darkMinus2'  : { hex: '#151a21', label: 'Neutral Dark -2' },
    'darkMinus3'  : { hex: '#0b0f14', label: 'Neutral Dark -3' },
    'black'       : { hex: '#000000', label: 'Black' },

    // Semantic

    // Success
    'successPlus5'  : { hex: '#ebfbf4', label: 'Success +5' },
    'successPlus4'  : { hex: '#bcefdb', label: 'Success +4' },
    'successPlus3'  : { hex: '#8ddfc0', label: 'Success +3' },
    'successPlus2'  : { hex: '#5ecda3', label: 'Success +2' },
    'successPlus1'  : { hex: '#2fb785', label: 'Success +1' },
    'success0'      : { hex: '#009e66', label: 'Success 0 (Base)' },
    'successMinus1' : { hex: '#008b58', label: 'Success -1' },
    'successMinus2' : { hex: '#00764a', label: 'Success -2' },
    'successMinus3' : { hex: '#005e3a', label: 'Success -3' },
    'successMinus4' : { hex: '#004329', label: 'Success -4' },
    'successMinus5' : { hex: '#002617', label: 'Success -5' },

    // Error
    'errorPlus5'  : { hex: '#ffebeb', label: 'Error +5' },
    'errorPlus4'  : { hex: '#ffbcbc', label: 'Error +4' },
    'errorPlus3'  : { hex: '#ff8d8d', label: 'Error +3' },
    'errorPlus2'  : { hex: '#ff5e5e', label: 'Error +2' },
    'errorPlus1'  : { hex: '#f82f2f', label: 'Error +1' },
    'error0'      : { hex: '#ea0000', label: 'Error 0 (Base)' },
    'errorMinus1' : { hex: '#cf0000', label: 'Error -1' },
    'errorMinus2' : { hex: '#ac0000', label: 'Error -2' },
    'errorMinus3' : { hex: '#830000', label: 'Error -3' },
    'errorMinus4' : { hex: '#5a0000', label: 'Error -4' },
    'errorMinus5' : { hex: '#300000', label: 'Error -5' }
}

/** Shared property options --------------------------------- */

/**
 * The null option.
 * 
 * Normalized property for null/default options.
 */
export const nullOption = { value: '', label: '— Select —' }

/**
 * The custom option.
 * 
 * Normalized property for custom options.
 */
export const customOption = { value: 'custom', label: 'Custom...' }

/**
 * Side options.
 * 
 * Represents possible values for any component that may have a box-model-based property that applies to a side.
 */
export const sides = [
    { value: 'top', label: 'Top' },
    { value: 'right', label: 'Right' },
    { value: 'bottom', label: 'Bottom' },
    { value: 'left', label: 'Left' },
]

/**
 * Horizontal alignment options.
 * 
 * Represents possible values for any component that may have a horizontal alignment property.
 */
export const horizontalAlignments = [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'center', label: 'Center' },
    { value: 'between', label: 'Space Between' },
    { value: 'around', label: 'Space Around' }
]

/**
 * Vertical alignment options.
 * 
 * Represents possible values for any component that may have a vertical alignment property.
 */
export const verticalAlignments = [
    { value: 'stretch', label: 'Stretch' },
    { value: 'top', label: 'Top' },
    { value: 'center', label: 'Center' },
    { value: 'bottom', label: 'Bottom' }
]

/**
 * Matrix alignment options.
 * 
 * Represents possible values for any component that may have a matrix alignment property.
 */
export const matrixAlignments = [
    { value: 'topLeft', label: 'Top Left' },
    { value: 'topCenter', label: 'Top Center' },
    { value: 'topRight', label: 'Top Right' },
    { value: 'centerLeft', label: 'Center Left' },
    { value: 'center', label: 'Center' },
    { value: 'centerRight', label: 'Center Right' },
    { value: 'bottomLeft', label: 'Bottom Left' },
    { value: 'bottomCenter', label: 'Bottom Center' },
    { value: 'bottomRight', label: 'Bottom Right' },
]

/**
 * Fit options.
 * 
 * Represents possible values for any component that may have a fit property.
 */
export const fits = [
    { value: 'contain', label: 'Contain' },
    { value: 'cover', label: 'Cover' },
    { value: 'fill', label: 'Fill' },
    { value: 'none', label: 'None' },
    { value: 'scaleDown', label: 'Scale Down' }
]

/**
 * Matrix alignment options.
 * 
 * Represents possible values for any component that may have a matrix alignment property.
 */

/**
 * Named spacing options.
 * 
 * Represents all named spacings in the theme.
 */
export const spacings = [
    { value: 'none', label: 'None' },
    { value: 'module', label: 'Module' },
    { value: 'block', label: 'Block' },
    { value: 'section', label: 'Section' },
    { value: 'hero', label: 'Hero' }
]

/**
 * Named color context options.
 * 
 * Represents all named color contexts in the theme.
 */
export const colorContexts = [
    { value: 'white', label: 'White', color: 'white' },
    { value: 'lightGray', label: 'Light Gray', color: 'lightPlus3' },
    { value: 'orange', label: 'Orange', color: 'wOrange0' },
    { value: 'lightOrange', label: 'Light Orange', color: 'wOrangePlus5' },
    { value: 'teal', label: 'Teal', color: 'wTeal0' },
]

/** Getters ------------------------------------------------- */

/**
 * Generalized getter function for shared properties.
 */
function getSharedOptions( options, whitelist = [], includeNull = true, includeCustom = false ) {

    if ( !! whitelist.length  ) {
        options = options.filter( ( option ) => whitelist.indexOf( option.value ) !== -1 )
    }

    if ( includeNull ) {
        options = [ nullOption, ...options ]
    }

    if ( includeCustom ) {
        options = [ ...options, customOption ]
    }

    return options
}

/**
 * Gets horizontal alignments.
 * 
 * @see getSharedOptions()
 */
export function getSides( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( sides, whitelist, includeNull, includeCustom );
}

/**
 * Gets horizontal alignments.
 * 
 * @see getSharedOptions()
 */
export function getHorizontalAlignments( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( horizontalAlignments, whitelist, includeNull, includeCustom );
}

/**
 * Gets vertical alignments.
 * 
 * @see getSharedOptions()
 */
export function getVerticalAlignments( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( verticalAlignments, whitelist, includeNull, includeCustom );
}

/**
 * Gets matrix alignments.
 * 
 * @see getSharedOptions()
 */
export function getMatrixAlignments( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( matrixAlignments, whitelist, includeNull, includeCustom );
}

/**
 * Gets fits.
 * 
 * @see getSharedOptions()
 */
export function getFits( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( fits, whitelist, includeNull, includeCustom );
}

/**
 * Gets named spacings.
 * 
 * @see getSharedOptions()
 */
export function getSpacings( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( spacings, whitelist, includeNull, includeCustom );
}

/**
 * Gets color contexts.
 * 
 * @see getSharedOptions()
 */
export function getColorContexts( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( colorContexts, whitelist, includeNull, includeCustom )
}
 